import { Box, Typography, Paper, Grid, Select, MenuItem, FormControl, InputLabel, Chip, Alert, Divider } from '@mui/material';
import { ArrowRight, CheckCircle, AlertCircle, Link2 } from 'lucide-react';
import { ParsedCSV } from '../../../utils/csvParser';
import { FieldMapping, ImportType, RelationshipMapping, RELATIONSHIP_CONFIGS } from '../BulkImport';

interface MappingStepProps {
  parsedCSV: ParsedCSV;
  fieldMappings: FieldMapping[];
  onMappingsChange: (mappings: FieldMapping[]) => void;
  relationshipMappings: RelationshipMapping[];
  onRelationshipMappingsChange: (mappings: RelationshipMapping[]) => void;
  importType: ImportType;
}

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  studentId: 'Student ID',
  email: 'Email',
  phone: 'Phone',
  gradeLevel: 'Grade Level',
  dateOfBirth: 'Date of Birth',
  address: 'Address',
  city: 'City',
  state: 'State',
  zipCode: 'Zip Code',
  organization: 'Organization',
  role: 'Role',
  name: 'Name',
  description: 'Description',
  amount: 'Amount',
  category: 'Category',
  dueDate: 'Due Date',
  isRequired: 'Is Required',
  allowMultiple: 'Allow Multiple',
  maxQuantity: 'Max Quantity',
  instructor: 'Instructor',
  capacity: 'Capacity',
  schedule: 'Schedule',
  location: 'Location',
  startDate: 'Start Date',
  endDate: 'End Date'
};

const REQUIRED_FIELDS: Record<ImportType, string[]> = {
  students: ['firstName', 'lastName', 'studentId', 'email'],
  parents: ['firstName', 'lastName', 'email'],
  community: ['firstName', 'lastName', 'email'],
  fees: ['name', 'amount', 'category'],
  courses: ['name', 'instructor']
};

export const MappingStep = ({
  parsedCSV,
  fieldMappings,
  onMappingsChange,
  relationshipMappings,
  onRelationshipMappingsChange,
  importType
}: MappingStepProps) => {
  const relationshipConfigs = RELATIONSHIP_CONFIGS[importType] || [];

  const handleMappingChange = (systemField: string, csvColumn: string) => {
    const columnIndex = csvColumn === '' ? null : parsedCSV.columns.findIndex(col => col.name === csvColumn);

    const newMappings = fieldMappings.map(mapping => {
      if (mapping.systemField === systemField) {
        return {
          ...mapping,
          csvColumn: csvColumn === '' ? null : csvColumn,
          csvColumnIndex: columnIndex === -1 ? null : columnIndex
        };
      }
      if (mapping.csvColumn === csvColumn && csvColumn !== '') {
        return {
          ...mapping,
          csvColumn: null,
          csvColumnIndex: null
        };
      }
      return mapping;
    });

    onMappingsChange(newMappings);
  };

  const handleRelationshipMappingChange = (relationshipId: string, csvColumn: string) => {
    const columnIndex = csvColumn === '' ? null : parsedCSV.columns.findIndex(col => col.name === csvColumn);

    const newMappings = relationshipMappings.map(mapping => {
      if (mapping.relationshipId === relationshipId) {
        return {
          ...mapping,
          csvColumn: csvColumn === '' ? null : csvColumn,
          csvColumnIndex: columnIndex === -1 ? null : columnIndex
        };
      }
      return mapping;
    });

    onRelationshipMappingsChange(newMappings);
  };

  const isFieldRequired = (field: string) => {
    return REQUIRED_FIELDS[importType].includes(field);
  };

  const mappedCount = fieldMappings.filter(m => m.csvColumn !== null).length;
  const requiredMappedCount = fieldMappings.filter(m =>
    m.csvColumn !== null && isFieldRequired(m.systemField)
  ).length;
  const requiredFieldsTotal = REQUIRED_FIELDS[importType].length;
  const relationshipMappedCount = relationshipMappings.filter(m => m.csvColumn !== null).length;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Map CSV Columns to System Fields
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Match each column from your CSV file to the corresponding system field. Fields marked as required must be mapped.
      </Alert>

      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>Mapped Fields:</strong> {mappedCount} / {fieldMappings.length}
          </Typography>
          <Typography variant="body2">
            <strong>Required Fields:</strong> {requiredMappedCount} / {requiredFieldsTotal}
          </Typography>
          {requiredMappedCount === requiredFieldsTotal ? (
            <Chip
              icon={<CheckCircle size={16} />}
              label="All required fields mapped"
              color="success"
              size="small"
            />
          ) : (
            <Chip
              icon={<AlertCircle size={16} />}
              label="Missing required fields"
              color="warning"
              size="small"
            />
          )}
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {fieldMappings.map((mapping) => {
          const column = parsedCSV.columns.find(col => col.name === mapping.csvColumn);
          const required = isFieldRequired(mapping.systemField);

          return (
            <Grid item xs={12} md={6} key={mapping.systemField}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {FIELD_LABELS[mapping.systemField] || mapping.systemField}
                  </Typography>
                  {required && (
                    <Chip label="Required" size="small" color="error" />
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>CSV Column</InputLabel>
                    <Select
                      value={mapping.csvColumn || ''}
                      onChange={(e) => handleMappingChange(mapping.systemField, e.target.value)}
                      label="CSV Column"
                    >
                      <MenuItem value="">
                        <em>Not Mapped</em>
                      </MenuItem>
                      {parsedCSV.columns.map((col) => (
                        <MenuItem
                          key={col.name}
                          value={col.name}
                          disabled={fieldMappings.some(m =>
                            m.csvColumn === col.name && m.systemField !== mapping.systemField
                          )}
                        >
                          {col.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <ArrowRight size={20} style={{ opacity: 0.5 }} />
                </Box>

                {column && column.sampleData.length > 0 && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Sample Data:
                    </Typography>
                    {column.sampleData.map((sample, idx) => (
                      <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {sample || <em>(empty)</em>}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {requiredMappedCount < requiredFieldsTotal && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          Please map all required fields before proceeding to the preview step.
        </Alert>
      )}

      {relationshipConfigs.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Link2 size={24} />
            <Typography variant="h6">
              Relationship Mapping (Optional)
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Map columns from your CSV to link these records to existing data in the system. This is optional - you can also set up relationships later.
          </Alert>

          {relationshipMappedCount > 0 && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  <strong>Relationships Configured:</strong> {relationshipMappedCount} / {relationshipConfigs.length}
                </Typography>
                <Chip
                  icon={<Link2 size={16} />}
                  label={`${relationshipMappedCount} relationship${relationshipMappedCount !== 1 ? 's' : ''} will be resolved`}
                  color="primary"
                  size="small"
                />
              </Box>
            </Paper>
          )}

          <Grid container spacing={2}>
            {relationshipConfigs.map((config) => {
              const mapping = relationshipMappings.find(m => m.relationshipId === config.id);
              const column = mapping?.csvColumn
                ? parsedCSV.columns.find(col => col.name === mapping.csvColumn)
                : null;

              return (
                <Grid item xs={12} md={6} key={config.id}>
                  <Paper sx={{ p: 2, border: '1px dashed', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Link2 size={18} style={{ opacity: 0.6 }} />
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>
                        {config.label}
                      </Typography>
                      <Chip
                        label="Optional"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
                      {config.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>CSV Column</InputLabel>
                        <Select
                          value={mapping?.csvColumn || ''}
                          onChange={(e) => handleRelationshipMappingChange(config.id, e.target.value)}
                          label="CSV Column"
                        >
                          <MenuItem value="">
                            <em>Not Mapped</em>
                          </MenuItem>
                          {parsedCSV.columns.map((col) => (
                            <MenuItem key={col.name} value={col.name}>
                              {col.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <ArrowRight size={20} style={{ opacity: 0.5 }} />
                    </Box>

                    {column && column.sampleData.length > 0 && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Sample Data:
                        </Typography>
                        {column.sampleData.map((sample, idx) => (
                          <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {sample || <em>(empty)</em>}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
};
